import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import {Carousel} from '@udemy-v2/react-structure-components';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import classNames from 'classnames';

import {publishEvent} from '@udemy-v2/event-tracking';
import {useOnCarouselScrollEnd} from '../../../../lib/hooks/use-on-carousel-scroll-end';
import {Review} from '@/types/review';
import {ReviewsModalButton} from './review-modal/reviews-modal-button.react-component';
import {ReviewsModalContainer} from './review-modal/reviews-modal-container.react-component';
import {ReviewTitle} from './review-title.react-component';
import {Review as ReviewComponent} from './review/review.react-component';
import {ReviewSkeleton} from './review/review-skeleton.react-component';
import styles from './reviews.module.scss';
import type {ReviewsConfig, ReviewsContext} from './types';
import {ReviewsCarouselScrollEvent, SeeMoreReviewsClickEvent} from '../../events/events';
import {
    ReviewsModalSource,
    useClpContext,
} from '../../context/clp-context-provider.react-component';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {useI18n} from '@udemy-v2/i18n';
import {ReviewRequestConfig} from '@/lib/hooks/use-reviews';

export interface ReviewsProps {
    reviewsConfig: ReviewRequestConfig;
    reviews: Review[];
    isLoading: boolean;
    reviewsRatings?: ReviewsContext;
    reviewableObjectId: number;
    reviewableObjectType: string;
    className?: string;
    sectionHeaderClassName?: string;
    mobileCarouselClassName?: string;
    tabletCarouselClassName?: string;
    scrollTargetClassName?: string;
    courseId: number;
    hideReviewTitle?: boolean;
    showModalSummaryWidget?: boolean;
    /**
     * Identifies which reviews section this component belongs to.
     * Used to ensure only the correct modal opens when there are multiple Reviews components.
     */
    modalSource?: ReviewsModalSource;
}

export const noReviewsMessage = (store: ReviewsConfig, gettext: (text: string) => string) => {
    if (!store.rating) {
        if (!store.searchText) {
            return {
                purpose: 'no-comments-message',
                message: gettext('There are no written comments for this course yet.'),
            };
        }
        return {
            purpose: 'no-matched-comments-message',
            message: gettext('No reviews matched your search. Try searching with another term.'),
        };
    }
    if (!store.searchText) {
        return {
            purpose: 'no-selected-comments-message',
            message: gettext(
                "There are no written comments for the rating you've selected. " +
                    'Please select another rating or clear your selection to view ' +
                    'all written comments for this course.',
            ),
        };
    }
    return {
        purpose: 'no-selected-matched-comments-message',
        message: gettext(
            'Your search returned no results with the selected rating. ' +
                'Try clearing your selection to see reviews matching your search.',
        ),
    };
};

export const Reviews = ({
    reviewsConfig,
    reviews,
    isLoading,
    reviewsRatings,
    reviewableObjectId,
    reviewableObjectType,
    className,
    sectionHeaderClassName,
    mobileCarouselClassName,
    tabletCarouselClassName,
    scrollTargetClassName,
    courseId,
    hideReviewTitle,
    showModalSummaryWidget = true,
    modalSource = 'variation-reviews',
}: ReviewsProps) => {
    const {isReviewsModalOpen, reviewsModalSource, openReviewsModal, closeReviewsModal} =
        useClpContext();
    const course = useClpCourse();
    const isXsMax = useMatchMedia('xs-max');
    const isSmMin = useMatchMedia('sm-min');
    const isSmMax = useMatchMedia('sm-max');
    const {gettext} = useI18n();

    // Only show this modal if it matches the source that triggered the open
    const isThisModalOpen = isReviewsModalOpen && reviewsModalSource === modalSource;

    const handleOnModalTriggerButtonClick = () => {
        if (!isReviewsModalOpen && course.frontendTrackingId) {
            publishEvent(
                new SeeMoreReviewsClickEvent({
                    courseTrackingId: course.frontendTrackingId,
                    reviewableObjectId: courseId,
                    reviewableObjectType: 'course',
                }),
            );
        }
        openReviewsModal(modalSource);
    };

    const reviewsModal = (
        <ReviewsModalContainer
            showModalSummaryWidget={showModalSummaryWidget}
            reviewsConfig={reviewsConfig}
            reviewsRatings={reviewsRatings}
            frontendTrackingId={course.frontendTrackingId}
            reviewableObjectType={reviewableObjectType}
            reviewableObjectId={reviewableObjectId}
            isXsMax={isXsMax ?? false}
            isOpen={isThisModalOpen}
            renderTitle={() =>
                reviewsRatings ? (
                    <ReviewTitle reviewsContext={reviewsRatings} isXsMax={isXsMax ?? false} />
                ) : (
                    <TextSkeleton
                        className={classNames(styles['reviews-title'], sectionHeaderClassName)}
                        paragraphCount={0}
                        withTitle={true}
                    />
                )
            }
            onClose={closeReviewsModal}
            shouldUseTwoColumnLayout={!isXsMax && (isSmMin ?? false)}
        />
    );

    if (isLoading || !reviewsRatings) {
        return (
            <div data-purpose="reviews-loading" id="reviews" className={className}>
                <TextSkeleton
                    className={classNames(styles['reviews-title'], sectionHeaderClassName)}
                    paragraphCount={0}
                    withTitle={true}
                />
                {isSmMax ? (
                    <Carousel
                        allowScroll
                        ariaLabel="Course reviews"
                        data-purpose="reviews-mobile-carousel-layout"
                        showPager={!isXsMax}
                        pagerButtonSize="medium"
                        className={classNames(
                            mobileCarouselClassName,
                            !isXsMax ? tabletCarouselClassName : null,
                            styles.loading,
                        )}
                    >
                        <ReviewSkeleton className={styles.review} />
                        <ReviewSkeleton className={styles.review} />
                    </Carousel>
                ) : (
                    <div
                        data-purpose="reviews-desktop-layout"
                        className={styles['reviews-desktop']}
                    >
                        <ReviewSkeleton className={styles.review} isDesktopLayout={true} />
                        <ReviewSkeleton className={styles.review} isDesktopLayout={true} />
                        <ReviewSkeleton className={styles.review} isDesktopLayout={true} />
                        <ReviewSkeleton className={styles.review} isDesktopLayout={true} />
                    </div>
                )}
                {reviewsModal}
            </div>
        );
    }

    if (!reviews || reviews.length === 0) {
        return null;
    }

    const content = (
        <>
            {isSmMax ? (
                <ReviewsContentMobile
                    reviews={reviews}
                    frontendTrackingId={course.frontendTrackingId}
                    reviewableObjectType={reviewableObjectType}
                    reviewableObjectId={reviewableObjectId}
                    isXsMax={isXsMax ?? false}
                    mobileCarouselClassName={mobileCarouselClassName}
                    tabletCarouselClassName={tabletCarouselClassName}
                    onModalTriggerButtonClick={handleOnModalTriggerButtonClick}
                    courseId={courseId}
                />
            ) : (
                <ReviewsContentDesktop
                    reviews={reviews}
                    frontendTrackingId={course.frontendTrackingId}
                    reviewableObjectType={reviewableObjectType}
                    reviewableObjectId={reviewableObjectId}
                    onModalTriggerButtonClick={handleOnModalTriggerButtonClick}
                />
            )}
            <ReviewsModalButton
                frontendTrackingId={course.frontendTrackingId}
                reviewableObjectType={reviewableObjectType}
                reviewableObjectId={reviewableObjectId}
                fullWidth={isXsMax ?? false}
                onClick={handleOnModalTriggerButtonClick}
                className={styles['trigger-button-container']}
                label={gettext('Show all reviews')}
            />
            {reviewsModal}
        </>
    );

    return (
        <div data-purpose="reviews" className={className}>
            <span className={scrollTargetClassName ?? ''} id="reviews" />
            {!hideReviewTitle && (
                <ReviewTitle
                    reviewsContext={reviewsRatings}
                    isXsMax={isXsMax ?? false}
                    className={classNames(styles['reviews-title'], sectionHeaderClassName)}
                />
            )}
            {content}
        </div>
    );
};

interface ReviewsContentMobileProps {
    reviews: Review[];
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    isXsMax: boolean;
    mobileCarouselClassName?: string;
    tabletCarouselClassName?: string;
    onModalTriggerButtonClick: () => void;
    courseId: number;
}
const ReviewsContentMobile = ({
    reviews,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    isXsMax,
    mobileCarouselClassName,
    tabletCarouselClassName,
    onModalTriggerButtonClick,
    courseId,
}: ReviewsContentMobileProps) => {
    const course = useClpCourse();
    const carouselScroll = useOnCarouselScrollEnd(trackCarouselScroll);

    function trackCarouselScroll() {
        if (course.frontendTrackingId) {
            publishEvent(
                new ReviewsCarouselScrollEvent({
                    courseTrackingId: course.frontendTrackingId,
                    reviewableObjectId: courseId,
                    reviewableObjectType: 'course',
                }),
            );
        }
    }

    if (reviews.length === 0) {
        return null;
    }

    return (
        <div>
            <Carousel
                allowScroll
                ariaLabel="Course reviews"
                data-purpose="reviews-mobile-carousel-layout"
                showPager={!isXsMax}
                pagerButtonSize="medium"
                gridMode={true}
                gridFullWidthItems={true}
                gridSize="medium"
                className={classNames(
                    mobileCarouselClassName,
                    !isXsMax ? tabletCarouselClassName : null,
                )}
                {...carouselScroll.props}
            >
                {reviews.map((review) => (
                    <ReviewComponent
                        key={review.id}
                        review={review}
                        frontendTrackingId={frontendTrackingId}
                        reviewableObjectType={reviewableObjectType}
                        reviewableObjectId={reviewableObjectId}
                        className={styles.review}
                        shouldUseInlineShowMore={false}
                        shouldUseDeattachedDropdown={true}
                        instructorResponseVisibility="seo-only"
                        onShowMoreModalTriggerClick={onModalTriggerButtonClick}
                        isInMobileCarousel={true}
                    />
                ))}
            </Carousel>
        </div>
    );
};

interface ReviewsContentDesktopProps {
    reviews: Review[];
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    onModalTriggerButtonClick: () => void;
}
export const DESKTOP_MAX_NUMBER_OF_DISPLAYED_REVIEWS = 4;
const ReviewsContentDesktop = ({
    reviews,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    onModalTriggerButtonClick,
}: ReviewsContentDesktopProps) => {
    return (
        <div data-purpose="reviews-desktop-layout" className={styles['reviews-desktop']}>
            {reviews.slice(0, DESKTOP_MAX_NUMBER_OF_DISPLAYED_REVIEWS).map((review) => (
                <ReviewComponent
                    key={review.id}
                    review={review}
                    frontendTrackingId={frontendTrackingId}
                    reviewableObjectType={reviewableObjectType}
                    reviewableObjectId={reviewableObjectId}
                    className={styles.review}
                    isDesktopLayout={true}
                    shouldUseInlineShowMore={false}
                    instructorResponseVisibility="seo-only"
                    onShowMoreModalTriggerClick={onModalTriggerButtonClick}
                />
            ))}
        </div>
    );
};

interface NoReviewsMessageProps {
    message: {
        purpose: string;
        message: string;
    };
}

export const NoReviewsMessage = ({message}: NoReviewsMessageProps) => {
    return (
        <p
            className={classNames('ud-text-md', styles['no-reviews-message'])}
            data-testid={message.purpose}
        >
            {message.message}
        </p>
    );
};
